import { sdkRequest } from '@eb/http';
import { collectionsDuck } from '@eb/redux-destination';
import { useQuery, useQueryClient } from 'react-query';
import { Collection, User, UserCollections } from '../types';

const FIVE_MINUTES = 5 * 60 * 1000;

export const getUser = async (): Promise<User> => {
    const user = await sdkRequest('/api/v3/users/me/');
    return user;
};

const getUserCollections = async (id: string): Promise<UserCollections> => {
    const collections = await collectionsDuck.api.getUserCollections(id);
    return { collections: collections.collections };
};

interface UseUserResponse {
    fetchUserData: Function;
    collections?: Collection[];
    id?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
}

export const useUser = (): UseUserResponse => {
    const queryClient = useQueryClient();

    //Get user object to grab logged in user ID if not passed through
    const { data: userObject, refetch: fetchUser } = useQuery('user', getUser, {
        enabled: false,
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        staleTime: FIVE_MINUTES,
    });

    const userId = userObject?.id;

    const { data, refetch: fetchUserCollections } = useQuery(
        ['userCollections', userId],
        () => getUserCollections(userId as string),
        {
            enabled: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchOnMount: false,
            staleTime: FIVE_MINUTES,
        },
    );

    const fetchUserData = (user?: User) => {
        if (user && !userId) {
            //Imperatively set the cache data since we have the user object
            //via props passed to the event card
            queryClient.setQueryData('user', user);
            return getUserCollections(user?.id).then((data) =>
                queryClient.setQueryData(['userCollections', user?.id], data),
            );
        } else if (!userId) {
            return fetchUser().then(() => fetchUserCollections());
        } else if (!data?.collections) {
            return fetchUserCollections();
        }

        return Promise.resolve();
    };

    return {
        fetchUserData,
        id: userId,
        firstName: userObject?.first_name,
        lastName: userObject?.last_name,
        name: userObject?.name,
        collections: data?.collections,
    };
};
